import { z } from 'zod';

import {
  AccountAccessFlowProcessingReasonSchema,
  AccountAccessFlowStatusSchema,
  AccountAccessStatusSchema,
} from './shared';

export const AccountAccessScopeSchema = z.enum([
  'ACCOUNTS',
  'IDENTIFICATION',
  'BALANCES',
]);

export const AccountAccessFlowScaCodeValuesSchema = z.enum([
  'CHIP_OTP',
  'PHOTO_OTP',
  'PUSH_OTP',
  'SMS_OTP',
  'REDIRECT',
]);

export const AccountAccessFlowRequiredInputSchema = z.enum([
  'INSTITUTION_ID',
  'PSU_ID',
  'SCA_USERNAME',
  'SCA_PASSWORD',
  'SCA_CODE',
  'SCA_METHOD',
]);

const ScaAllowedValuesSchema = z.object({
  description: z.string().nullable(),
  name: AccountAccessFlowScaCodeValuesSchema,
  value: z.string(),
});

const ScaMethodRequiredInputSchema = z.object({
  allowedValues: z.array(ScaAllowedValuesSchema),
  name: z.literal(AccountAccessFlowRequiredInputSchema.enum.SCA_METHOD),
  propertyPath: z.string(),
});

const OneOfAllowedValuesRequiredInputSchema = ScaMethodRequiredInputSchema;

const StringRequiredInputSchema = z.object({
  name: AccountAccessFlowRequiredInputSchema.exclude([
    AccountAccessFlowRequiredInputSchema.enum.SCA_METHOD,
  ]),
  propertyPath: z.string(),
});

export const AccountAccessFlowInformationSystemSchema =
  z.literal('OPEN_BANKING');

export const AccountAccessFlowFinalSchema = z.object({
  status: z.enum([
    AccountAccessFlowStatusSchema.enum.FINISHED,
    AccountAccessFlowStatusSchema.enum.ABORTED,
    AccountAccessFlowStatusSchema.enum.EXCEPTION,
  ]),
});

export const AccountAccessFlowProcessingWaitingForRedirectUrlSchema = z.object({
  status: z.literal(AccountAccessFlowStatusSchema.enum.PROCESSING),
  details: z.object({
    reason: z.enum([
      AccountAccessFlowProcessingReasonSchema.enum.AWAITING_REDIRECT_URL,
      AccountAccessFlowProcessingReasonSchema.enum
        .AWAITING_DECOUPLED_AUTHORISATION,
    ]),
  }),
});

export const AccountAccessFlowProcessingWaitingForUserRedirectSchema = z.object(
  {
    status: z.literal(AccountAccessFlowStatusSchema.enum.PROCESSING),
    details: z.object({
      reason: z.literal(
        AccountAccessFlowProcessingReasonSchema.enum.AWAITING_USER_REDIRECT
      ),
      redirect: z.object({
        url: z.string().url(),
        directUrl: z.string().url(),
      }),
    }),
  }
);

export const AccountAccessFlowProcessingSchema = z.union([
  AccountAccessFlowProcessingWaitingForRedirectUrlSchema,
  AccountAccessFlowProcessingWaitingForUserRedirectSchema,
]);

export const AccountAccessFlowWaitingForInputSchema = z.object({
  status: z.literal(AccountAccessFlowStatusSchema.enum.WAITING_FOR_INPUT),
  requiredInput: z.array(
    z.union([StringRequiredInputSchema, OneOfAllowedValuesRequiredInputSchema])
  ),
});

export const AccountAccessFlowSchema = z.union([
  AccountAccessFlowWaitingForInputSchema,
  AccountAccessFlowProcessingSchema,
  AccountAccessFlowFinalSchema,
]);

export const AccountAccessOpenBankingSchema = z.object({
  institutionId: z.string().nullable(),
  accountIdentifiers: z.object({
    psuId: z.string().nullable().optional(),
  }),
});

export const AccountAccessSubjectSchema = z.object({
  reference: z.string(),
  email: z.string().email().nullable(),
  accountIdentifiers: z.object({
    psuId: z.string().nullable().optional(),
  }),
});

export const AccountAccessDeviceSchema = z.object({
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  fingerprint: z.string().nullable(),
});

export const GlobalApiAccountAccessSchema = z.object({
  id: z.string().uuid(),
  internalReference: z.string().max(100).nullable(),
  informationSystem: AccountAccessFlowInformationSystemSchema,
  openBanking: AccountAccessOpenBankingSchema,
  subject: AccountAccessSubjectSchema,
  scopes: z.array(AccountAccessScopeSchema).nonempty(),
  device: AccountAccessDeviceSchema,
  status: z.object({
    status: AccountAccessStatusSchema,
  }),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  accountAccessFlow: AccountAccessFlowSchema,
});
